import { useUserConfigurations } from "@via/api/configuration";
import { PageHeading } from "@via/components/page-heading.tsx";
import { statusOf } from "@via/pages/apps/hpc/hpc-apps.api.tsx";
import {
  decideErrorText,
  outcomeText,
  useDecideJobRequest,
  useJobRequest,
  type DecideJobRequestInput,
} from "@via/pages/agent-workspaces/workspaces.api.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import {
  type AgentWorkspaceJobRequestView,
  type JobRequestStateView,
  workspaceRefusalCategory,
} from "common";
import { type JSX, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

export const STILL_CHECKING_TEXT = "Still checking this job's folders";
export const STALLED_HEADLINE_TEXT =
  "The folder check for this job did not finish.";

/**
 * Why the folder check did not finish, in plain words, from the refusal code the request kept
 * (agent-7-4). A code only ever arrives here, never a transport's text.
 */
const PREVIEW_REFUSAL_TEXT: Record<string, string> = {
  bind_probe_unavailable:
    "Foundry could not reach the run environment to check the folders. This is often temporary.",
  agent_offline:
    "The run environment's agent was offline, so the folders could not be checked.",
  not_found:
    "One of the workspace's folders does not exist on the run environment.",
  agent_refused:
    "The run environment's agent refused one of the workspace's folders.",
  outside_data_roots:
    "One of the workspace's folders is outside the data folders this run environment allows.",
  outside_root_after_resolution:
    "One of the workspace's folders leads outside the data folders this run environment allows.",
  no_data_roots:
    "This run environment allows no data folders yet, so the workspace's folders cannot be mounted.",
  mode_exceeds_root:
    "One of the workspace's folders asks to write where the run environment allows reading only.",
  agent_home_unknown:
    "One of the workspace's folders is in your home folder on the run environment, and Foundry does not know where that is yet.",
  overlaps_workspace_tree:
    "One of the workspace's folders overlaps a workspace's own folder, which is never allowed.",
  container_runtime_not_configured:
    "This run environment has no container runtime set up for workspace jobs.",
  no_live_grant:
    "You no longer have access to this workspace's run environment.",
  workspace_not_ready: "The workspace is not ready to run jobs.",
  workspace_not_found: "The workspace no longer exists.",
  workspace_gone: "The workspace no longer exists.",
  workspace_mismatch:
    "The workspace is no longer on the run environment it was made on.",
  workspace_layout:
    "The workspace's folders on the run environment are not laid out as Foundry expects.",
  environment_gone: "The workspace's run environment no longer exists.",
  storage: "Your workspace storage on this run environment is at its limit.",
  live_workspaces:
    "You have as many live workspaces on this run environment as it allows.",
  live_jobs:
    "You have as many live workspace jobs on this run environment as it allows.",
  window_cpu:
    "Your workspace jobs used all the CPU minutes this run environment allows in its usage window.",
  workspace_not_accepting: "The workspace does not accept jobs right now.",
};

/** A category's sentence, for a code with none of its own. */
const CATEGORY_REFUSAL_TEXT: Partial<Record<string, string>> = {
  mount_policy:
    "One of the workspace's folders is outside what this run environment allows.",
  quota:
    "You have reached one of your workspace limits on this run environment.",
  ceiling:
    "The request is larger than this run environment allows for one job.",
};

/**
 * The sentence for a refusal code: its own, its category's (mount policy, quota, ceiling), or a
 * generic one. The jobs list uses it too, for a job refused before it started (agent-6-6).
 */
export const previewRefusalText = (code: string): string =>
  PREVIEW_REFUSAL_TEXT[code] ??
  CATEGORY_REFUSAL_TEXT[workspaceRefusalCategory(code)] ??
  "The check was refused for a reason this page cannot describe.";

/**
 * The preview runs inside the submit call that created the request, so a request still without
 * one this long after it was made had its check refused or cut short. The request keeps the
 * refusal's code when there was one, and only a submit with the same key runs the check again
 * (agent-7-4).
 */
export const PREVIEW_STALLED_AFTER_MS = 2 * 60 * 1000;

/** True once a request waiting for its preview is past the cutoff, re-rendering at that moment. */
const usePreviewStalled = (view?: AgentWorkspaceJobRequestView): boolean => {
  const made =
    view?.state === "awaiting_approval" && view.preview === null
      ? Date.parse(view.createdAt)
      : NaN;
  const deadline = Number.isFinite(made)
    ? made + PREVIEW_STALLED_AFTER_MS
    : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (deadline === null || now >= deadline) return;
    const timer = setTimeout(() => setNow(Date.now()), deadline - now);
    return () => clearTimeout(timer);
  }, [deadline, now]);
  return deadline !== null && now >= deadline;
};

const STATE_TEXT: Readonly<Record<JobRequestStateView, string>> = {
  awaiting_approval: "Waiting for your approval",
  approved: "Approved",
  rejected: "Not run",
  expired: "Expired",
};

const NETWORK_TEXT: Readonly<
  Record<
    NonNullable<AgentWorkspaceJobRequestView["preview"]>["network"],
    string
  >
> = {
  none: "No network",
  host_allowed: "Network allowed by the owner",
};

const MODE_TEXT = { ro: "read only", rw: "read and write" } as const;

const secondaryButton =
  "rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";
const primaryButton =
  "rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50";

const formatTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : "";

/**
 * The job approval screen (agent-7-4, agent-d68, agent-d73), the link an AI client hands a person
 * for a job in `foundry` approval mode. It shows everything the preview hash binds: the whole
 * command, the resources, the image, the network mode and every mount with its mode. Both buttons
 * send the hash of the preview on screen, so a preview that changed meanwhile is refused (409) and
 * reloaded for a new review, never approved silently.
 */
export default function JobRequestReview(): JSX.Element | null {
  const { workspaceId, requestId } = useParams();
  const { data: configurations } = useUserConfigurations();
  const enabled = Boolean(configurations?.AGENT_WORKSPACE_JOBS_ENABLED);
  const request = useJobRequest(workspaceId, requestId, enabled);
  const decide = useDecideJobRequest(workspaceId, requestId);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stalled = usePreviewStalled(request.data);

  if (!enabled) return null;

  const back = workspaceId ? (
    <Link
      className="text-sm font-semibold text-indigo-600 hover:text-indigo-500"
      to={`${ROUTES.WORKSPACES}/${workspaceId}`}>
      Back to the workspace
    </Link>
  ) : null;

  if (request.isLoading) {
    return <p className="p-4 text-sm text-gray-600">Loading the job request</p>;
  }
  if (request.isError || !request.data) {
    return (
      <main className="space-y-3 p-4">
        <p className="text-sm text-gray-600">
          {statusOf(request.error) === 404
            ? "This job request was not found, or you did not make it."
            : "This job request could not be loaded."}
        </p>
        {back}
      </main>
    );
  }

  const view = request.data;
  const preview = view.preview;
  const renderedHash = view.previewHash;
  const decidable =
    view.state === "awaiting_approval" &&
    preview !== null &&
    renderedHash !== null &&
    notice === null;

  const onDecide = async (
    decision: DecideJobRequestInput["decision"]
  ): Promise<void> => {
    if (renderedHash === null) return;
    setError(null);
    try {
      const answer = await decide.mutateAsync({
        decision,
        previewHash: renderedHash,
      });
      setNotice(
        answer.kind === "rejected"
          ? "You chose not to run this job."
          : outcomeText(answer.outcome)
      );
    } catch (failure) {
      // The mutation's settle reloads the request, so a changed preview is shown with its new
      // hash and needs a new press of a button: nothing here approves it on the person's behalf.
      setError(decideErrorText(failure));
    }
  };

  return (
    <main className="space-y-6 p-4">
      <PageHeading title="Review a job" />
      {back}
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[12rem_1fr]">
        <dt className="font-semibold text-gray-900">Status</dt>
        <dd className="text-gray-700">{STATE_TEXT[view.state]}</dd>
        <dt className="font-semibold text-gray-900">Command</dt>
        <dd>
          <pre
            data-testid="job-request-command"
            className="max-h-96 overflow-auto whitespace-pre-wrap break-all rounded-md bg-gray-50 p-3 font-mono text-xs text-gray-900 ring-1 ring-inset ring-gray-200">
            {view.command}
          </pre>
        </dd>
        <dt className="font-semibold text-gray-900">Resources</dt>
        <dd className="text-gray-700">
          {`${view.cpus} cpus, ${view.memoryMb} MB, ${view.wallMinutes} min`}
        </dd>
        {preview ? (
          <>
            <dt className="font-semibold text-gray-900">Image</dt>
            <dd className="break-all font-mono text-gray-700">
              {preview.image}
            </dd>
            <dt className="font-semibold text-gray-900">Network</dt>
            <dd className="text-gray-700">{NETWORK_TEXT[preview.network]}</dd>
            <dt className="font-semibold text-gray-900">Mounts</dt>
            <dd className="text-gray-700">
              {preview.mounts.length === 0 ? (
                "No folders are mounted."
              ) : (
                <ul className="space-y-1">
                  {preview.mounts.map(mount => (
                    <li
                      key={mount.containerPath}
                      className="flex flex-wrap gap-x-2">
                      <span className="break-all font-mono">
                        {mount.hostPath}
                      </span>
                      <span aria-hidden="true">at</span>
                      <span className="break-all font-mono">
                        {mount.containerPath}
                      </span>
                      <span>{MODE_TEXT[mount.mode]}</span>
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </>
        ) : null}
        {view.expiresAt ? (
          <>
            <dt className="font-semibold text-gray-900">Expires</dt>
            <dd className="text-gray-700">{formatTime(view.expiresAt)}</dd>
          </>
        ) : null}
      </dl>
      {view.state === "awaiting_approval" && preview === null && !stalled ? (
        <p className="text-sm text-gray-600" role="status">
          {STILL_CHECKING_TEXT}
        </p>
      ) : null}
      {view.state === "awaiting_approval" && preview === null && stalled ? (
        <div className="space-y-1 text-sm text-gray-700" role="status">
          <p className="font-semibold text-gray-900">{STALLED_HEADLINE_TEXT}</p>
          {view.lastPreviewRefusal ? (
            <p data-testid="job-request-preview-refusal">
              {`${previewRefusalText(view.lastPreviewRefusal)} Code: `}
              <code className="font-mono text-gray-900">
                {view.lastPreviewRefusal}
              </code>
            </p>
          ) : null}
          <p>
            {`It was asked for at ${formatTime(view.createdAt)}. Nothing runs until the check finishes. To try again, have your AI client submit this job again with the same submission key: `}
            <code
              data-testid="job-request-submission-key"
              className="break-all font-mono text-gray-900">
              {view.submissionKey}
            </code>
          </p>
        </div>
      ) : null}
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="text-sm text-gray-900" role="status">
          {notice}
        </p>
      ) : null}
      {decidable ? (
        <div className="flex gap-3">
          <button
            type="button"
            className={primaryButton}
            disabled={decide.isPending || request.isFetching}
            onClick={() => onDecide("approve")}>
            Run this job
          </button>
          <button
            type="button"
            className={secondaryButton}
            disabled={decide.isPending || request.isFetching}
            onClick={() => onDecide("reject")}>
            Do not run
          </button>
        </div>
      ) : null}
    </main>
  );
}
